import { Injectable } from '@angular/core';
import { Observable, forkJoin } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CustomersService } from './customers.service';
import { ContractsService } from './contracts.service';
import { InteractionsService, AdminInteraction } from './interactions.service';
import { UploadService } from './upload.service';
import { Customer } from '../interfaces/customer.interface';
import { InsuranceCompany } from '../interfaces/insurance-company.interface';
import { Contract } from '../interfaces/contract.interface';
import { AddEmailRequest, AddPhoneRequest } from '../interfaces/upload.interface';

@Injectable({
  providedIn: 'root'
})
export class CustomerDetailsManagementService {

  constructor(
    private customersService: CustomersService,
    private contractsService: ContractsService,
    private interactionsService: InteractionsService,
    private uploadService: UploadService,
    private snackBar: MatSnackBar
  ) {}

  /**
   * Load customer data by ID
   */
  loadCustomerData(customerId: number): Observable<any> {
    return this.customersService.getCustomerDetails(customerId);
  }

  /**
   * Load company data by ID
   */
  loadCompanyData(companyId: number): Observable<any> {
    return this.customersService.getCompanyDetails(companyId);
  }

  /**
   * Load contracts for customer
   */
  loadCustomerContracts(customerId: number): Observable<Contract[]> {
    return this.contractsService.getContractsByEntity('customer', customerId);
  }

  /**
   * Load contracts for company
   */
  loadCompanyContracts(companyId: number): Observable<Contract[]> {
    return this.contractsService.getContractsByEntity('company', companyId);
  }

  /**
   * Load interactions for customer
   */
  loadCustomerInteractions(customerId: number): Observable<AdminInteraction[]> {
    return this.interactionsService.getCustomerInteractionsForLogs(customerId);
  }

  /**
   * Load interactions for company
   */
  loadCompanyInteractions(companyId: number): Observable<AdminInteraction[]> {
    return this.interactionsService.getCompanyInteractionsForLogs(companyId);
  }

  /**
   * Add email to company
   */
  addCompanyEmail(companyId: number, email: string): Observable<any> {
    const emailData: AddEmailRequest = { email: email.trim() };
    return this.uploadService.addCompanyEmail(companyId, emailData);
  }

  /**
   * Add phone to company
   */
  addCompanyPhone(companyId: number, phone: string): Observable<any> {
    const phoneData: AddPhoneRequest = { phone: phone.trim() };
    return this.uploadService.addCompanyPhone(companyId, phoneData);
  }

  /**
   * Add email to customer
   */
  addCustomerEmail(customerId: number, email: string): Observable<any> {
    const emailData: AddEmailRequest = { email: email.trim() };
    return this.uploadService.addCustomerEmail(customerId, emailData);
  }

  /**
   * Add phone to customer
   */
  addCustomerPhone(customerId: number, phone: string): Observable<any> {
    const phoneData: AddPhoneRequest = { phone: phone.trim() };
    return this.uploadService.addCustomerPhone(customerId, phoneData);
  }

  /**
   * Update contact valid status for company
   */
  updateCompanyContactValidStatus(companyId: number, contactValue: string, validStatus: boolean | null, contactType: 'official' | 'email' | 'phone'): Observable<any> {
    const updateData = {
      contact_value: contactValue,
      valid_status: validStatus,
      contact_type: contactType
    };
    return this.uploadService.updateContactValidStatus(companyId, updateData);
  }

  /**
   * Update contact valid status for customer
   */
  updateCustomerContactValidStatus(customerId: number, contactValue: string, validStatus: boolean | null, contactType: 'official' | 'email' | 'phone'): Observable<any> {
    const updateData = {
      contact_value: contactValue,
      valid_status: validStatus,
      contact_type: contactType
    };
    return this.uploadService.updateCustomerContactValidStatus(customerId, updateData);
  }

  /**
   * Update contact value for company
   */
  updateCompanyContactValue(companyId: number, oldValue: string, newValue: string, contactType: 'email' | 'phone'): Observable<any> {
    const updateData = {
      old_value: oldValue,
      new_value: newValue,
      contact_type: contactType
    };
    return this.uploadService.updateContactValue(companyId, updateData);
  }

  /**
   * Update contact value for customer
   */
  updateCustomerContactValue(customerId: number, oldValue: string, newValue: string, contactType: 'email' | 'phone'): Observable<any> {
    const updateData = {
      old_value: oldValue,
      new_value: newValue,
      contact_type: contactType
    };
    return this.uploadService.updateCustomerContactValue(customerId, updateData);
  }

  /**
   * Show success message
   */
  showSuccess(message: string): void {
    this.snackBar.open(message, 'Bezárás', {
      duration: 3000,
      panelClass: ['success-snackbar']
    });
  }

  /**
   * Show error message
   */
  showError(message: string): void {
    this.snackBar.open(message, 'Bezárás', {
      duration: 5000,
      panelClass: ['error-snackbar']
    });
  }
}